import React, { Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import axios from '../../utils/axiosConfig';
import { getWatcherStatusData } from '../../redux/actions/watcherActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import Table, { SortableTh } from '../../components/Table';
import useSort from '../../components/Table/useSort';
// import TextInput from '../../components/TextInput';
import Pagination from '../../components/Pagination';
import Loader from '../../components/Loader';
import WatcherError from '../../components/Error';
import WatcherStatusStyles from './WatcherStatus.module.css';

const FRONTEND_WATCHER_STATUS_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildWatcherStatusSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const WatcherStatusScreen = () => {
	const dispatch = useDispatch();
	const [ searchKey, setSearchKey ] = React.useState('');

	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ watcherStatusData, setWatcherStatusData ] = React.useState({});
	const [ watcherTotalRows, setWatcherTotalRows ] = React.useState(0);
	const [ watcherRows, setWatcherRows ] = React.useState([]);
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	const [ pageNumber, setPageNumber ] = React.useState(1);
	const pageLimit = 21;

	// ── Appliance updates (C2C is the update server) ──────────────────────────
	const [ upd, setUpd ] = React.useState({ watchers: [], can_update: false,
		release_available: false, available_version: null });
	const [ updBusy, setUpdBusy ] = React.useState(false);
	const [ policyBusy, setPolicyBusy ] = React.useState(false);

	const loadUpdatable = React.useCallback(async () => {
		try {
			const { data } = await axios.get('/api/watchers/updatable');
			if (data && data.status) setUpd(data);
		} catch (e) { /* leave prior */ }
	}, []);

	React.useEffect(() => { loadUpdatable(); }, [ loadUpdatable ]);

	const triggerUpdate = async (id, name) => {
		setUpdBusy(true);
		try {
			const { data } = await axios.post(`/api/watchers/${id}/update`, {});
			if (data && data.status) { toast.success(data.msg || `Update queued for ${name}`); loadUpdatable(); }
			else toast.error(data?.msg || 'Could not queue the update');
		} catch (e) { toast.error(e?.response?.data?.detail || 'Update request failed'); }
		setUpdBusy(false);
	};

	const triggerUpdateAll = async () => {
		if (!window.confirm('Queue an update for every watcher you manage?')) return;
		setUpdBusy(true);
		try {
			const { data } = await axios.post('/api/watchers/update-all', {});
			if (data && data.status) { toast.success(data.msg || 'Updates queued'); loadUpdatable(); }
			else toast.error(data?.msg || 'Could not queue updates');
		} catch (e) { toast.error(e?.response?.data?.detail || 'Update request failed'); }
		setUpdBusy(false);
	};

	// Push current C2C policy to a box now (it pulls on its next ~15s poll).
	const applyPolicy = async (id, name) => {
		setPolicyBusy(true);
		try {
			const { data } = await axios.post(`/api/watchers/${id}/apply-policy`, {});
			if (data && data.status) toast.success(data.msg || `Policy push queued for ${name}`);
			else toast.error(data?.msg || 'Could not queue policy push');
		} catch (e) { toast.error(e?.response?.data?.detail || 'Policy push failed'); }
		setPolicyBusy(false);
	};

	const applyPolicyAll = async () => {
		if (!window.confirm('Push the current policy to every watcher you manage now?')) return;
		setPolicyBusy(true);
		try {
			const { data } = await axios.post('/api/watchers/apply-policy-all', {});
			if (data && data.status) toast.success(data.msg || 'Policy push queued');
			else toast.error(data?.msg || 'Could not queue policy push');
		} catch (e) { toast.error(e?.response?.data?.detail || 'Policy push failed'); }
		setPolicyBusy(false);
	};

	const setWatcherStatusSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildWatcherStatusSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearWatcherStatusSupportError = React.useCallback((force = false) => {
		if (!force) return;
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	const loadStatus = React.useCallback(async () => {
		setDataLoading(true);
		try {
			const data = await dispatch(getWatcherStatusData());
			if (data && data.status && Array.isArray(data.watchercols) && Array.isArray(data.watcherrows)) {
				setWatcherStatusData(data);
				clearWatcherStatusSupportError(true);
			} else {
				setWatcherStatusData({});
				setWatcherStatusSupportError(
					FRONTEND_WATCHER_STATUS_ERROR_CODES.INITIAL_LOAD_FAILED,
					'Watcher Status Page / Initial Data Load Failed',
					data?.msg || (typeof data === 'string' ? data : 'Watcher status request did not return success')
				);
			}
		} catch (error) {
			setWatcherStatusData({});
			setWatcherStatusSupportError(
				FRONTEND_WATCHER_STATUS_ERROR_CODES.INITIAL_LOAD_FAILED,
				'Watcher Status Page / Initial Data Request Error',
				error?.message || String(error)
			);
		} finally {
			setDataLoading(false);
		}
	}, [ clearWatcherStatusSupportError, dispatch, setWatcherStatusSupportError ]);

	React.useEffect(() => { loadStatus(); }, [ loadStatus ]);

	// ── Access-server editor (note which server reaches an imported Watcher) ──
	const [ accessEdit, setAccessEdit ] = React.useState(null);
	const [ accessSaving, setAccessSaving ] = React.useState(false);

	// No-telemetry acknowledgement (why an offline/never-reporting box is expected).
	const [ ackEdit, setAckEdit ] = React.useState(null);
	const [ ackSaving, setAckSaving ] = React.useState(false);

	const openAckEdit = (rowKey) => {
		const m = watcherStatusData.watchermeta && watcherStatusData.watchermeta[rowKey];
		if (!m) return;
		setAckEdit({ id: m.id, name: rowKey, reason: m.telemetry_ack_reason || '' });
	};

	const saveTelemetryAck = async (reasonOverride) => {
		if (!ackEdit) return;
		const reason = reasonOverride !== undefined ? reasonOverride : ackEdit.reason;
		setAckSaving(true);
		try {
			const { data } = await axios.post(`/api/watchers/${ackEdit.id}/telemetry-ack`, { reason });
			if (data && data.status) {
				toast.success(data.msg || 'Saved');
				setAckEdit(null);
				await loadStatus();
			} else {
				toast.error(data?.msg || 'Could not save acknowledgement');
			}
		} catch (e) {
			toast.error(e?.response?.data?.detail || 'Save failed');
		}
		setAckSaving(false);
	};

	const openAccessEdit = (rowKey) => {
		const m = watcherStatusData.watchermeta && watcherStatusData.watchermeta[rowKey];
		if (!m) return;
		setAccessEdit({
			id: m.id, name: rowKey,
			access_server: m.access_server || '',
			server_port: m.server_port != null ? String(m.server_port) : '',
			reverse_port: m.reverse_port != null ? String(m.reverse_port) : '',
			console_reverse_port: m.console_reverse_port != null ? String(m.console_reverse_port) : '',
			ssh_user: m.ssh_user || '',
		});
	};

	const saveAccess = async () => {
		if (!accessEdit) return;
		setAccessSaving(true);
		try {
			const { data } = await axios.post(`/api/watchers/${accessEdit.id}/access`, {
				access_server: accessEdit.access_server,
				server_port: accessEdit.server_port,
				reverse_port: accessEdit.reverse_port,
				console_reverse_port: accessEdit.console_reverse_port,
				ssh_user: accessEdit.ssh_user,
			});
			if (data && data.status) {
				toast.success(data.msg || 'Access server saved');
				setAccessEdit(null);
				await loadStatus();
			} else {
				toast.error(data?.msg || 'Could not save access details');
			}
		} catch (e) {
			toast.error(e?.response?.data?.detail || 'Save failed');
		}
		setAccessSaving(false);
	};

	// Rows are arrays, so the sort field is the column index. Feed useSort the
	// searched set, then page-slice the sorted result below.
	const filteredStatusRows = React.useMemo(() => {
		const rows =
			watcherStatusData && Array.isArray(watcherStatusData.watcherrows)
				? watcherStatusData.watcherrows
				: [];
		if (!searchKey) return rows;
		return rows.filter((row) =>
			row.find((el) => String(el ?? '').toLowerCase().includes(String(searchKey ?? '').toLowerCase()))
		);
	}, [ watcherStatusData, searchKey ]);
	const statusSort = useSort(filteredStatusRows);

	// Default the page to Status-sorted with Online on top. Applied once, after
	// the columns load (they arrive asynchronously, so useSort's `initial` can't
	// see them at mount), and found by column NAME so it survives a column
	// reorder. A ref keeps it a one-time default: a later manual sort — or a data
	// refresh after an access edit — won't yank the sort back. The comparator
	// ranks "Online" above "Offline", so ascending puts online boxes first.
	const didDefaultSort = React.useRef(false);
	React.useEffect(() => {
		if (didDefaultSort.current) return;
		const cols = watcherStatusData && watcherStatusData.watchercols;
		if (!Array.isArray(cols) || cols.length === 0) return;
		const idx = cols.findIndex((c) => String(c).toLowerCase() === 'status');
		if (idx >= 0) {
			statusSort.setSort(idx, 'asc');
			didDefaultSort.current = true;
		}
	}, [ watcherStatusData, statusSort ]);

	React.useEffect(
		() => {
			const sortedRows = Array.isArray(statusSort.sorted) ? statusSort.sorted : [];
			setWatcherTotalRows(sortedRows.length);
			const start = (pageNumber - 1) * pageLimit;
			setWatcherRows(sortedRows.slice(start, start + pageLimit));
		},
		[ statusSort.sorted, pageNumber ]
	);

	// A new search or sort re-slices from the top, so snap back to page 1.
	React.useEffect(() => {
		setPageNumber(1);
	}, [ searchKey, statusSort.sortKey, statusSort.sortDir ]);

	const handlePageChange = async (currentPage) => {
		setPageNumber(currentPage.selected + 1);
	};

	return (
		<Content
			currentMenu='watcher'
			headerTitle='Watcher Status'
			searchBox={true}
			searchPlaceholder='Search here'
			searchValue={searchKey}
			searchOnChange={(e) => setSearchKey(e.target.value)}>
			{dataLoading ? (
				<Loader />
			) : watcherStatusData && Object.keys(watcherStatusData).length === 0 ? (
				<div>
					<WatcherError />
					{supportErrorMessage && (
						<div className={WatcherStatusStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={WatcherStatusStyles.supportErrorCode}>{supportErrorMessage}</code>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={WatcherStatusStyles.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={WatcherStatusStyles.copySupportErrorButton}
									onClick={() => clearWatcherStatusSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
				</div>
			) : (
				<Fragment>
					{upd.release_available && (
						<div style={{ border: '1px solid var(--light-grey)', borderRadius: 8,
							padding: '14px 16px', marginBottom: 16, background: 'var(--white)' }}>
							<div style={{ display: 'flex', justifyContent: 'space-between',
								alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
								<div style={{ color: 'var(--black)' }}>
									<strong>Appliance Updates</strong> — available version{' '}
									<code>{upd.available_version}</code>
								</div>
								{upd.can_update && (
									<div style={{ display: 'flex', gap: 8 }}>
										<button type='button' disabled={policyBusy} onClick={applyPolicyAll}
											title='Push the current C2C policy (IDS rules, allowlists, denied lists, blocking, OT) to every watcher now — they pull on their next ~15s poll'
											style={{ background: 'transparent', border: '1px solid var(--primary-color)',
												color: 'var(--primary-color)', borderRadius: 6, padding: '8px 14px',
												fontWeight: 600, cursor: 'pointer' }}>
											{policyBusy ? 'Pushing…' : 'Apply Policy to All'}
										</button>
										{upd.watchers.some((w) => w.update_available) && (
											<button type='button' disabled={updBusy} onClick={triggerUpdateAll}
												style={{ background: 'var(--primary-color)', color: '#fff', border: 'none',
													borderRadius: 6, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' }}>
												{updBusy ? 'Queuing…' : 'Update All Watchers'}
											</button>
										)}
									</div>
								)}
							</div>
							<table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
								<thead><tr>
									{['Watcher', 'Current', 'Status', ''].map((h) => (
										<th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 12,
											textTransform: 'uppercase', color: 'var(--grey)',
											borderBottom: '1px solid var(--light-grey)' }}>{h}</th>
									))}
								</tr></thead>
								<tbody>
									{upd.watchers.map((w) => (
										<tr key={w.id}>
											<td style={{ padding: '6px 8px', color: 'var(--black)' }}>{w.name}</td>
											<td style={{ padding: '6px 8px', color: 'var(--black)' }}>
												{w.version || '—'}
											</td>
											<td style={{ padding: '6px 8px', color: 'var(--grey)' }}>
												{[ 'scheduled', 'queued', 'downloading', 'installing', 'failed' ].includes(w.update_status?.state)
													? `${w.update_status.state}${w.update_status.error ? ': ' + w.update_status.error : ''}`
													: (w.update_available ? 'Update available' : 'Up to date')}
											</td>
											<td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
												{upd.can_update && (
													<button type='button' disabled={policyBusy}
														onClick={() => applyPolicy(w.id, w.name)}
														title='Push current policy to this watcher now'
														style={{ background: 'transparent', border: '1px solid var(--light-grey)',
															color: 'var(--grey)', borderRadius: 6, padding: '4px 10px', marginRight: 6,
															fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>
														Apply Policy
													</button>
												)}
												{upd.can_update && w.update_available && (
													<button type='button' disabled={updBusy}
														onClick={() => triggerUpdate(w.id, w.name)}
														style={{ background: 'transparent', border: '1px solid var(--primary-color)',
															color: 'var(--primary-color)', borderRadius: 6, padding: '4px 12px',
															fontWeight: 600, cursor: 'pointer' }}>
														Update
													</button>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
					{supportErrorMessage && (
						<div className={WatcherStatusStyles.supportErrorBanner}>
							<div className={WatcherStatusStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={WatcherStatusStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={WatcherStatusStyles.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={WatcherStatusStyles.copySupportErrorButton}
									onClick={() => clearWatcherStatusSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
					{/* <div className={WatcherStatusStyles.filterContainer}>
						<div className={WatcherStatusStyles.filterContainer_box} style={{ alignItems: 'flex-end' }}>
							<TextInput
								label='Search'
								icon='search'
								value={searchKey}
								onChange={(e) => setSearchKey(e.target.value)}
								placeholder='Search here'
							/>
							<Button label='Filter' />
						</div>
					</div> */}

					<Table
						containerStyle={{ height: '75vh' }}
						tableHead={
							<tr>
								{watcherStatusData.watchercols &&
									watcherStatusData.watchercols.length > 0 &&
									watcherStatusData.watchercols.map((col, index) => (
										<SortableTh key={col + index} label={col} field={index} sort={statusSort} />
									))}
							</tr>
						}
						tableRow={
							watcherTotalRows > 0 && watcherRows.length > 0 ? (
								watcherRows.map((rows, index) => (
									<tr key={index}>
										{rows && rows.length > 0 && rows.map((row, colIndex) => {
											// Get the corresponding column name for this cell
											const colName = watcherStatusData.watchercols[colIndex];
											
											// Check if this is the Last Seen column and replace GMT with UTC
											if (colName === "Last Seen") {
												const displayValue = row ? String(row).replace(/GMT/g, 'UTC') : row;
												return (
													<td 
														key={displayValue + colIndex}
														className={WatcherStatusStyles.date_time}
													>
														{displayValue}
													</td>
												);
											}
											
											// Check if this is the Watcher ID column
											const isWatcherIdColumn = watcherStatusData.watchercols && 
												watcherStatusData.watchercols[colIndex] && 
												(watcherStatusData.watchercols[colIndex].toLowerCase().includes('watcherid') ||
												 watcherStatusData.watchercols[colIndex].toLowerCase().includes('watcher id') ||
												 watcherStatusData.watchercols[colIndex].toLowerCase().includes('watcher_id') ||
												 watcherStatusData.watchercols[colIndex].toLowerCase() === 'id');
											
											if (isWatcherIdColumn) {
												return (
													<td 
														key={row + colIndex} 
														className={WatcherStatusStyles.watcher_id}
													>
														{row}
													</td>
												);
											}
											
											// Check if this is the status column (status is usually in position 3)
											const isStatusColumn = watcherStatusData.watchercols && 
												watcherStatusData.watchercols[colIndex] && 
												watcherStatusData.watchercols[colIndex].toLowerCase().includes('status');
											
											if (isStatusColumn) {
												const isOnline = row.toLowerCase() === 'active' || row.toLowerCase() === 'online';
												const rowKey = rows[0];
												const m = watcherStatusData.watchermeta && watcherStatusData.watchermeta[rowKey];
												// The no-telemetry ack is only meaningful while the box is
												// offline; a reporting box shows its live status regardless.
												const ackReason = !isOnline && m ? m.telemetry_ack_reason : null;
												return (
													<td
														key={row + colIndex}
														className={ackReason ? undefined
															: (isOnline ? WatcherStatusStyles.status_online : WatcherStatusStyles.status_offline)}
														style={{ whiteSpace: 'nowrap' }}
													>
														<span className={isOnline ? WatcherStatusStyles.status_online : WatcherStatusStyles.status_offline}>
															{row}
														</span>
														{ackReason && (
															<span
																title={`Acknowledged${m.telemetry_ack_by ? ' by ' + m.telemetry_ack_by : ''}`
																	+ `${m.telemetry_ack_at ? ' · ' + m.telemetry_ack_at : ''}`}
																style={{ marginLeft: 8, color: 'var(--grey)', fontSize: 12, fontStyle: 'italic' }}
															>
																· ack: {ackReason}
															</span>
														)}
														{!isOnline && watcherStatusData.is_provider && (
															<button
																type='button'
																title={ackReason ? 'Edit / clear no-telemetry acknowledgement'
																	: 'Acknowledge no telemetry (give a reason)'}
																onClick={() => openAckEdit(rowKey)}
																style={{ marginLeft: 8, background: 'transparent',
																	border: '1px solid var(--primary-color)', color: 'var(--primary-color)',
																	borderRadius: 4, padding: '0px 6px', cursor: 'pointer', fontSize: 12 }}
															>
																{ackReason ? '✎' : 'Ack'}
															</button>
														)}
													</td>
												);
											}

											// Access Server: an "Open Console" link (when a console reverse
											// port is recorded) reaches the box GUI via the access server, and
											// an edit affordance for providers to note an imported box's tunnel.
											if (colName === 'Access Server') {
												const rowKey = rows[0];
												const m = watcherStatusData.watchermeta && watcherStatusData.watchermeta[rowKey];
												const consoleUrl = m && m.access_server && m.console_reverse_port
													? `https://${m.access_server}:${m.console_reverse_port}` : '';
												return (
													<td key={'access' + colIndex} style={{ whiteSpace: 'nowrap' }}>
														{row}
														{consoleUrl && (
															<a
																href={consoleUrl}
																target='_blank'
																rel='noopener noreferrer'
																title={`Open the appliance console via the access server (${consoleUrl})`}
																style={{ marginLeft: 8, color: 'var(--primary-color)',
																	fontSize: 12, fontWeight: 600, textDecoration: 'none' }}
															>
																Open Console ↗
															</a>
														)}
														{watcherStatusData.is_provider && (
															<button
																type='button'
																title='Edit access server'
																onClick={() => openAccessEdit(rowKey)}
																style={{ marginLeft: 8, background: 'transparent',
																	border: '1px solid var(--primary-color)', color: 'var(--primary-color)',
																	borderRadius: 4, padding: '0px 6px', cursor: 'pointer', fontSize: 12 }}
															>
																✎
															</button>
														)}
													</td>
												);
											}

											// Host-health cells (Disk / Memory / CPU): colour by threshold
											// so a filling disk reads at a glance. Display thresholds mirror
											// the SOC alerter's (disk 75/85, mem 85/95, cpu 150/300). "—"
											// (box hasn't reported) stays neutral grey.
											if (colName === 'Disk' || colName === 'Memory' || colName === 'CPU') {
												const pct = typeof row === 'string' && row.endsWith('%')
													? parseFloat(row) : null;
												const t = colName === 'Disk' ? [75, 85]
													: colName === 'Memory' ? [85, 95] : [150, 300];
												let color = 'var(--grey)';
												if (pct !== null && !Number.isNaN(pct)) {
													color = pct >= t[1] ? 'var(--red, #c0392b)'
														: pct >= t[0] ? 'var(--amber, #d98200)'
															: 'var(--green, #2e7d32)';
												}
												return (
													<td key={'health' + colIndex} style={{ whiteSpace: 'nowrap' }}>
														<span style={{ color, fontWeight: pct !== null && pct >= t[0] ? 600 : 400,
															fontVariantNumeric: 'tabular-nums' }}>
															{row}
														</span>
													</td>
												);
											}

											return <td key={row + colIndex}>{row}</td>;
										})}
									</tr>
								))
							) : (
								<tr>
									<td colSpan={watcherStatusData.watchercols.length} style={{ textAlign: 'center' }}>
										No data found
									</td>
								</tr>
							)
						}
					/>

					<div className='paginationContainer'>
						<Pagination
							onPageChange={handlePageChange}
							rowsPerPage={pageLimit}
							totalPageSize={watcherTotalRows}
							pageNumber={pageNumber}
						/>
					</div>

					{ackEdit && (
						<div
							style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,.45)',
								display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
							onClick={() => { if (!ackSaving) setAckEdit(null); }}
						>
							<div
								onClick={(e) => e.stopPropagation()}
								style={{ background: 'var(--white)', borderRadius: 10, padding: 24,
									width: 'min(460px, 92vw)', boxShadow: '0 10px 40px rgba(0,0,0,.25)' }}
							>
								<h3 style={{ margin: '0 0 4px', color: 'var(--black)' }}>Acknowledge No Telemetry — {ackEdit.name}</h3>
								<p style={{ margin: '0 0 16px', color: 'var(--grey)', fontSize: 13 }}>
									Mark this Watcher's lack of telemetry as expected and say why (e.g. “Watcher not
									installed yet”). Shown on this page while the box is offline — it records the reason
									only and does not change anything on the box.
								</p>
								<textarea
									value={ackEdit.reason}
									autoFocus
									onChange={(e) => setAckEdit({ ...ackEdit, reason: e.target.value })}
									placeholder='Watcher not installed yet'
									rows={3}
									style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6,
										border: '1px solid var(--light-grey)', fontSize: 14, resize: 'vertical' }}
								/>
								<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
									<button
										type='button' disabled={ackSaving} onClick={() => setAckEdit(null)}
										style={{ background: 'transparent', border: '1px solid var(--light-grey)',
											color: 'var(--black)', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}
									>Cancel</button>
									<button
										type='button' disabled={ackSaving} onClick={() => saveTelemetryAck('')}
										title='Remove the acknowledgement'
										style={{ background: 'transparent', border: '1px solid var(--grey)',
											color: 'var(--grey)', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}
									>Clear</button>
									<button
										type='button' disabled={ackSaving || !ackEdit.reason.trim()} onClick={() => saveTelemetryAck()}
										style={{ background: 'var(--primary-color)', color: '#fff', border: 'none',
											borderRadius: 6, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' }}
									>{ackSaving ? 'Saving…' : 'Save'}</button>
								</div>
							</div>
						</div>
					)}

					{accessEdit && (
						<div
							style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,.45)',
								display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
							onClick={() => { if (!accessSaving) setAccessEdit(null); }}
						>
							<div
								onClick={(e) => e.stopPropagation()}
								style={{ background: 'var(--white)', borderRadius: 10, padding: 24,
									width: 'min(460px, 92vw)', boxShadow: '0 10px 40px rgba(0,0,0,.25)' }}
							>
								<h3 style={{ margin: '0 0 4px', color: 'var(--black)' }}>Access Server — {accessEdit.name}</h3>
								<p style={{ margin: '0 0 16px', color: 'var(--grey)', fontSize: 13 }}>
									Note which server this Watcher is reached through. This records the reference
									only — it does not provision or change anything on the box.
								</p>
								{[
									{ k: 'access_server', label: 'Access Server (host)', ph: 'access.example.com' },
									{ k: 'server_port', label: 'SSH Port', ph: '22' },
									{ k: 'reverse_port', label: 'SSH Reverse Port (→ :22)', ph: 'e.g. 22001' },
									{ k: 'console_reverse_port', label: 'GUI Reverse Port (→ :8443)', ph: 'e.g. 84001' },
									{ k: 'ssh_user', label: 'SSH User', ph: 'watcher141' },
								].map((f) => (
									<label key={f.k} style={{ display: 'block', marginBottom: 12 }}>
										<span style={{ display: 'block', fontSize: 12, color: 'var(--grey)', marginBottom: 4 }}>{f.label}</span>
										<input
											value={accessEdit[f.k]}
											placeholder={f.ph}
											onChange={(e) => setAccessEdit({ ...accessEdit, [f.k]: e.target.value })}
											style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--light-grey)',
												borderRadius: 6, boxSizing: 'border-box' }}
										/>
									</label>
								))}
								<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
									<button type='button' disabled={accessSaving} onClick={() => setAccessEdit(null)}
										style={{ background: 'transparent', border: '1px solid var(--light-grey)',
											borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>
										Cancel
									</button>
									<button type='button' disabled={accessSaving} onClick={saveAccess}
										style={{ background: 'var(--primary-color)', color: '#fff', border: 'none',
											borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}>
										{accessSaving ? 'Saving…' : 'Save'}
									</button>
								</div>
							</div>
						</div>
					)}
				</Fragment>
			)}
		</Content>
	);
};

export default WatcherStatusScreen;
